<template>
  <v-dialog fullscreen scrollable persistent :value="value">
    <v-card color="kbLight">
      <v-toolbar color="kbLight" flat max-height="64px">
        <span class="text-h5 kbDarkBlueText--text text-capitalize font-weight-medium">
          {{ method === 'edit' ? $t('general.editRule') : $t('general.newRule') }}
        </span>
        <v-spacer></v-spacer>
        <v-btn color="kbDarkBlueText" icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider/>

      <v-card-text v-if="loadingRule">
        <LoadingComponent height="75vh"
                          :message="$t('loadingRule')" />
      </v-card-text>

      <v-card-text v-else-if="method === 'edit' && !loadingRule && Object.keys(sequencesRule).length === 0">
        <NotFound :message="$t('general.ruleNotFound')" />
      </v-card-text>

      <v-card-text v-else>
        <v-form ref="form">
          <v-card color="kbWhite" class="mt-4">
            <v-card-title class="kbDarkBlueText--text">1. {{ $t('general.ruleDetails') }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" class="pb-0">
                  <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                    {{ $t('general.name') }}
                  </span>
                  <v-text-field background-color="kbLight"
                                color="kbDarkBlueText"
                                :label="$t('general.enterRuleName')"
                                :rules="[rules.required]"
                                solo
                                v-model="rule.name"/>
                </v-col>

                <v-col cols="12" class="py-0">
                  <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                    {{ $t('general.description') }}
                  </span>
                  <v-textarea background-color="kbLight"
                              color="kbDarkBlueText"
                              :label="$t('general.enterRuleDescription')"
                              solo
                              v-model="rule.payload.description"/>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card color="kbWhite" class="mt-4">
            <v-card-title class="kbDarkBlueText--text">
              2. {{ $t('general.relateRuleToSequenceOrSetAsGlobal') }}
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" class="py-0">
                  <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                    {{ $t('general.setRuleAsGlobal') }}
                  </span>
                  <v-switch color="kbBlue"
                            v-model="rule.isGlobal" />
                </v-col>

                <v-col cols="12" class="py-0" v-show="!rule.isGlobal">
                  <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                    {{ $t('general.relateRuleToSequence') }}
                  </span>
                  <v-autocomplete background-color="kbLight"
                                  color="kbDarkBlueText"
                                  :items="sequences"
                                  :label="$t('general.selectASequence')"
                                  :rules="[!rule.isGlobal ? rules.required : true]"
                                  solo
                                  item-text="name"
                                  item-value="id"
                                  v-model="rule.sequenceId" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <TriggersList
            :selectedRuleSequenceId="rule.sequenceId"
            :triggers="rule.triggers"
            @addAction="addAction($event)"
            @addTrigger="addTrigger()"
            @deleteAction="deleteAction($event)"
            @deleteTrigger="deleteTrigger($event)"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="d-flex justify-end pt-4">
        <v-btn
          color="kbBlue"
          class="mr-2"
          outlined
          @click="toggleModal()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn
          color="kbBlue"
          dark
          :loading="loadingSubmit"
          @click="submit()">
          {{ method === 'edit' ? $t('general.edit') : $t('general.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import LoadingComponent from '@/components/ui/LoadingComponent'
import NotFound from '@/components/ui/NotFound'
import TriggersList from '@/pages/App/Sequences/Rules/dialogs/CreateEditRule/TriggersList'
export default {
  components: { LoadingComponent, NotFound, TriggersList },
  computed: {
    ...mapState({
      sequences: state => state.sequences.sequences,
      sequencesRule: state => state.sequences.sequencesRule
    })
  },
  created () {
    this.getSequences()
  },
  data () {
    return {
      loadingRule: false,
      loadingSubmit: false,
      rules,
      rule: {
        isGlobal: false,
        triggers: [],
        name: '',
        payload: {
          description: ''
        },
        sequenceId: ''
      }
    }
  },
  methods: {
    addAction (triggerIndex) {
      const action = { actionType: '' }
      this.rule.triggers[triggerIndex].actions.push({ ...action })
    },
    addTrigger () {
      const trigger = {
        actions: [
          { actionType: '', payload: {} }
        ],
        triggerType: ''
      }
      this.rule.triggers.push({ ...trigger })
    },
    async createRule () {
      if (!this.$refs.form.validate()) return notifications.error(this.$t('general.youMustFillAllFieldsCorrectly'))
      try {
        this.loadingSubmit = true
        await this.$store.dispatch('createSequencesRule', {
          appId: this.$store.getters.appUuid,
          data: { ...this.rule }
        })
        this.loadingSubmit = false
        notifications.success(this.$t('general.ruleSuccessfullyCreated'))
        this.$emit('getRules')
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    deleteAction ({ actionIndex, triggerIndex }) {
      this.rule.triggers[triggerIndex].actions.splice(actionIndex, 1)
    },
    deleteTrigger (index) {
      this.rule.triggers.splice(index, 1)
    },
    async getRule () {
      try {
        this.loadingRule = true
        await this.$store.dispatch('getSequencesRule',
          {
            appId: this.$store.getters.appUuid,
            ruleId: this.selectedRuleId
          }
        )
        this.loadingRule = false
        this.setLocalRule()
      } catch (e) {
        this.loadingRule = false
        notifications.error(e)
      }
    },
    async getSequences () {
      try {
        await this.$store.dispatch('getSequences', { appId: this.$store.getters.appUuid })
      } catch (e) {
        notifications.error(e)
        this.toggleModal()
      }
    },
    resetRule () {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.rule = {
        isGlobal: false,
        triggers: [],
        name: '',
        payload: {
          description: ''
        },
        sequenceId: ''
      }
    },
    setLocalRule () {
      this.rule = { ...this.sequencesRule }
    },
    submit () {
      this.method === 'edit' ? this.updateRule() : this.createRule()
    },
    toggleModal () {
      this.$emit('input', false)
      this.resetRule()
    },
    async updateRule () {
      if (!this.$refs.form.validate()) return notifications.error(this.$t('general.youMustFillAllFieldsCorrectly'))
      try {
        this.loadingSubmit = true
        await this.$store.dispatch('updateSequencesRule', {
          appId: this.$store.getters.appUuid,
          ruleId: this.selectedRuleId,
          data: { ...this.rule }
        })
        this.loadingSubmit = false
        notifications.success(this.$t('general.ruleSuccessfullyUpdated'))
        this.$emit('getRules')
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    }
  },
  props: {
    method: {
      type: String,
      default: 'create'
    },
    selectedRuleId: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    value (value) {
      if (value && this.method === 'edit') {
        this.getRule()
      }
    }
  }
}
</script>
